import { afterRenderEffect, Component, computed, ElementRef, inject, NgZone, signal, viewChild } from '@angular/core';
import { MenuService } from '../../menu/menu.service';
import { CartService } from '../../services/cart.service';
import { ComboRegistryService } from '../../services/combo-registry.service';
import { rankRecommendationIds, recommendablePlacements, resolveRecommendations } from '../cart.utils';

@Component({
  selector: 'app-recommendations',
  templateUrl: './recommendations.component.html',
  styleUrl: './recommendations.component.scss',
  standalone: false,
})
export class RecommendationsComponent {
  private readonly menuService = inject(MenuService);
  private readonly cartService = inject(CartService);
  private readonly comboRegistry = inject(ComboRegistryService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngZone = inject(NgZone);

  private readonly slider = viewChild<ElementRef<HTMLElement>>('slider');

  // Scroll indicator drawn by hand rather than styling the native scrollbar - iOS WebKit has no
  // persistent, stylable scrollbar. Thumb width as % of the track; null when the row doesn't overflow.
  readonly thumbSize = signal<number | null>(null);

  private readonly placements = computed(() => recommendablePlacements(
    this.menuService.menu()?.sections ?? [],
    new Set(this.comboRegistry.getGroups().map(group => group.groupId)),
  ));

  // Live against both the cart and the menu: an item leaves the row once it's added and comes
  // back if it's removed again, and a menu.updated price or availability change reaches the
  // cards, with the next ranked item filling any gap.
  readonly items = computed(() => {
    const recommendations = this.menuService.recommendations();
    if (!recommendations) return [];

    const cartItemIds = new Set(this.cartService.cartLines().map(line => line.item.id));
    return resolveRecommendations(rankRecommendationIds(recommendations, cartItemIds), this.placements(), recommendations.max_items);
  });

  constructor() {
    // Re-measures when the row's width changes (ResizeObserver), when items come or go
    // (items() dependency) and on scroll. The scroll listener runs outside the zone so
    // scrolling doesn't change-detect the whole page each frame.
    afterRenderEffect((onCleanup) => {
      this.items();
      const el = this.slider()?.nativeElement;
      if (!el) return;

      const update = () => this.updateScrollThumb(el);
      const observer = new ResizeObserver(update);
      observer.observe(el);
      this.ngZone.runOutsideAngular(() => el.addEventListener('scroll', update, { passive: true }));
      update();
      onCleanup(() => {
        observer.disconnect();
        el.removeEventListener('scroll', update);
      });
    });
  }

  private updateScrollThumb(el: HTMLElement): void {
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 1) {
      this.thumbSize.set(null);
      return;
    }

    // Only the size goes through the signal - it changes on resize, not on scroll, so setting
    // the same value while scrolling is a no-op. The position is written straight to a CSS var.
    const size = (el.clientWidth / el.scrollWidth) * 100;
    this.thumbSize.set(size);

    // scrollLeft runs 0 → -maxScroll in RTL; the thumb is placed with inset-inline-start
    // so the same progress value works in both directions.
    const progress = Math.min(Math.abs(el.scrollLeft) / maxScroll, 1);
    this.host.nativeElement.style.setProperty('--thumb-offset', `${progress * (100 - size)}%`);
  }
}
